// SPDX-License-Identifier: AGPL-3.0-or-later

import Guilds from '@app/features/guild/state/Guilds';
import Tasks, {type Task} from '@app/features/tasks/state/Tasks';
import Users from '@app/features/user/state/Users';
import {makeAutoObservable} from 'mobx';

export interface TaskMention {
	task: Task;
	commentId: string;
	authorId: string;
	content: string;
	createdAt: string;
}

const SEEN_KEY = 'pioneerchat.taskMentionsSeenAt';

function readSeen(): string {
	try {
		return localStorage.getItem(SEEN_KEY) ?? '';
	} catch {
		return '';
	}
}

/** Comments on to-dos where someone tagged you with @, for the Activity page and its count. */
class TaskMentions {
	seenAt = readSeen();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get all(): Array<TaskMention> {
		const me = Users.currentUserId;
		if (!me) return [];
		const out: Array<TaskMention> = [];
		for (const guildId of Guilds.getGuildIds()) {
			for (const task of Tasks.getAllGuildTasks(guildId)) {
				for (const comment of task.comments ?? []) {
					if (comment.author_id === me || !(comment.mention_ids ?? []).includes(me)) continue;
					out.push({
						task,
						commentId: comment.id,
						authorId: comment.author_id,
						content: comment.content,
						createdAt: comment.created_at,
					});
				}
			}
		}
		return out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
	}

	get unseenCount(): number {
		return this.all.filter((mention) => mention.createdAt > this.seenAt).length;
	}

	isUnseen(mention: TaskMention): boolean {
		return mention.createdAt > this.seenAt;
	}

	markAllSeen(): void {
		const newest = this.all[0]?.createdAt;
		if (!newest || newest <= this.seenAt) return;
		this.seenAt = newest;
		try {
			localStorage.setItem(SEEN_KEY, newest);
		} catch {
			// Private mode: the count just comes back next time.
		}
	}
}

export default new TaskMentions();
